import { forwardRef, ReactNode, useCallback, useState } from "react";
import { Heading, Text } from "@otrium/atoms";
import { Flex } from "@otrium/core";
import { Trans, msg } from "@lingui/macro";
import { Trans as TransComponent } from "@lingui/react";
import { MessageDescriptor } from "@lingui/core";
import { Button } from "src/atoms/Button";
import {
  SizeRow,
  SizeText,
  NotifyMeText,
  Wrapper,
  SizeListContainer,
  DrawerFooter,
  CloseButtonWrapper,
} from "./MobileSizeOverlay.styled";
import { SizeFitLabelBanner } from "src/atoms/SizeFitLabelBanner";
import { CloseButton } from "src/atoms/CloseButton";
import { SmallSizeGuideHeader } from "src/organisms/SmallSizeGuideHeader";
import { SingleProduct } from "src/types/graphql.d";
import { BackInStockNotification } from "src/molecules/BackInStockNotification";
import { SizeMapping } from "src/hooks/useSizeSchemaFilter";
import { ConversionItemAttributes } from "src/utils/constructor-beacon";

const sizeFitLabelMessages: Record<string, MessageDescriptor> = {
  runs_small: msg`size_fit_label.runs_small`,
  runs_large: msg`size_fit_label.runs_large`,
};

interface Props {
  onClose: () => void;
  openSizeGuide?: () => void;
  hasSizeGuide: boolean;
  addToCartLoading: boolean;
  handleAddToCart: () => Promise<boolean>;
  options: {
    value: string;
    text: ReactNode;
    description: ReactNode | null;
    disabled?: boolean;
    stockQuantity?: number;
    size?: string;
    mappedSize?: string;
  }[];
  selected?: string;
  handleOption: (e: null, option: { value: string }) => void;
  withMobileOverlay?: boolean;
  isSizeFitsSmall?: boolean;
  sizeFitLabel?: "runs_small" | "runs_large";
  product?: SingleProduct;
  cnstrcItem?: ConversionItemAttributes;
}

const MobileSizeOverlay = forwardRef<HTMLDivElement, Props>(
  (
    {
      onClose,
      openSizeGuide,
      hasSizeGuide,
      addToCartLoading,
      handleAddToCart,
      options,
      selected,
      handleOption,
      withMobileOverlay = false,
      isSizeFitsSmall = false,
      sizeFitLabel,
      product,
      cnstrcItem,
    },
    ref
  ) => {
    const [notifyPopupSizeMapping, setNotifyPopupSizeMapping] =
      useState<SizeMapping | null>(null);
    const [notifyPopupSelectedSize, setNotifyPopupSelectedSize] = useState("");

    const handleClose = useCallback(() => {
      setNotifyPopupSizeMapping(null);
      onClose();
    }, [onClose]);

    const onOptionClick =
      (option: {
        value: string;
        disabled?: boolean;
        stockQuantity?: number;
        size?: string;
        mappedSize?: string;
      }) =>
      () => {
        if (option.stockQuantity === 0 && product) {
          setNotifyPopupSizeMapping({
            variationId: option.value,
            brandSize: option.size || "",
            stockQuantity: 0,
            mappings: [],
          });
          const formattedSize = option.mappedSize
            ? `${option.size} / ${option.mappedSize}`
            : option.size || "";
          setNotifyPopupSelectedSize(formattedSize);
        } else {
          handleOption(null, option);
        }
      };

    return (
      <Wrapper ref={ref} withMobileOverlay={withMobileOverlay}>
        <Flex
          flexDirection="column"
          pt={3}
          pb={0}
          px={3}
          sx={{ position: "relative" }}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            pb={2}
            sx={{ position: "relative" }}
          >
            <Heading
              as="h2"
              fontFamily="PlusJakartaSans"
              fontSize={18}
              fontWeight={600}
              color="tone.black"
            >
              <Trans>Select your size</Trans>
            </Heading>
            <CloseButtonWrapper>
              <CloseButton
                iconSize={24}
                onClick={handleClose}
                sx={{
                  padding: 0,
                  justifyContent: "end",
                }}
              />
            </CloseButtonWrapper>
          </Flex>
          {isSizeFitsSmall ? (
            <SmallSizeGuideHeader onOpenSizeGuide={openSizeGuide} my={3} />
          ) : (
            <Flex alignItems="center" justifyContent="space-between" pb={3}>
              <Text fontSize={14}>
                <Trans>Not sure about your size?</Trans>
              </Text>
              {hasSizeGuide && (
                <Text
                  fontSize={14}
                  textDecoration={"underline"}
                  onClick={openSizeGuide}
                  sx={{ marginTop: "auto", textAlign: "right" }}
                >
                  <Trans>Size guide</Trans>
                </Text>
              )}
            </Flex>
          )}
        </Flex>

        <SizeListContainer hasSizeFitLabel={!!sizeFitLabel}>
          <Flex flexDirection="column">
            {options.map((option) => {
              const isOutOfStock = option.stockQuantity === 0;
              const canNotify = isOutOfStock && product;
              return (
                <SizeRow
                  key={`size-${option.value}`}
                  selected={option.value === selected}
                  disabled={option.disabled && !canNotify}
                  $isOutOfStock={isOutOfStock}
                  onClick={onOptionClick(option)}
                >
                  <SizeText $isOutOfStock={isOutOfStock}>
                    {option.text}
                  </SizeText>
                  <Text
                    fontSize={0}
                    color={
                      option.value === selected
                        ? "tone.black"
                        : "tone.anthracite"
                    }
                    lineHeight={1.5}
                  >
                    {canNotify ? (
                      <NotifyMeText>
                        <Trans>Notify me</Trans>
                      </NotifyMeText>
                    ) : (
                      option.description
                    )}
                  </Text>
                </SizeRow>
              );
            })}
          </Flex>
          {sizeFitLabel && (
            <SizeFitLabelBanner fixedInSizeOverlay>
              <Text color="tone.black" fontSize={[0]}>
                <TransComponent
                  id={sizeFitLabelMessages[sizeFitLabel].id}
                  message={sizeFitLabelMessages[sizeFitLabel].message}
                />
              </Text>
            </SizeFitLabelBanner>
          )}
        </SizeListContainer>

        <DrawerFooter {...cnstrcItem}>
          {!addToCartLoading ? (
            <Button
              variant="primary"
              {...(cnstrcItem && { "data-cnstrc-btn": "add_to_cart" })}
              onClick={async () => {
                const success = await handleAddToCart();
                if (success) {
                  handleClose();
                }
              }}
              disabled={selected === undefined}
              fullWidth
            >
              <Trans>Add to bag</Trans>
            </Button>
          ) : (
            <Button variant="primary" disabled fullWidth>
              <Trans>Adding to bag</Trans>
            </Button>
          )}
        </DrawerFooter>

        {product && notifyPopupSizeMapping && (
          <BackInStockNotification
            isOpen={!!notifyPopupSizeMapping}
            onClose={() => setNotifyPopupSizeMapping(null)}
            product={product}
            sizeMapping={notifyPopupSizeMapping}
            selectedSize={notifyPopupSelectedSize}
            isMobile={true}
          />
        )}
      </Wrapper>
    );
  }
);

MobileSizeOverlay.displayName = "MobileSizeOverlay";

export default MobileSizeOverlay;
